import * as THREE from 'three';
import { BGM_POOLS, BGM_TRACKS, MUSIC_ASSET_BASE_PATH } from '../config/bgm-tracks.js';

export function createBiomeBgmController(sounds) {
  const DIMENSIONS = ['overworld', 'nether', 'end'];
  const OPENING_TRACK_IDS = ['bouncing_trees', 'floating_trees_up'];
  const recentTrackIds = [];

  const blendState = {
    dimensions: {
      overworld: 1,
      nether: 0,
      end: 0
    },
    moods: {
      calm: 1,
      forest: 0,
      tense: 0
    }
  };

  let poolTelemetry = {
    dominantPoolId: 'overworld',
    dominantPoolLabel: BGM_POOLS.overworld?.label || 'overworld',
    contributions: {
      overworld: 1,
      overworld_forest: 0,
      overworld_tense: 0,
      nether: 0,
      end: 0
    },
    topCandidates: []
  };

  function damp(current, target, delta, speed = 2.2) {
    const t = 1 - Math.exp(-Math.max(0, speed) * Math.max(0, delta));
    return current + ((target - current) * t);
  }

  function getPoolLabel(poolId) {
    const pool = BGM_POOLS[poolId];
    if (!pool) return poolId;
    if (typeof pool.label === 'string' && pool.label) return pool.label;
    return poolId;
  }

  function getPoolEntries(poolId) {
    const pool = BGM_POOLS[poolId];
    if (!pool) return [];

    if (Array.isArray(pool)) {
      return pool.map((id) => ({ id, weight: 1 }));
    }

    if (!Array.isArray(pool.tracks)) return [];
    return pool.tracks
      .map((entry) => {
        if (typeof entry === 'string') return { id: entry, weight: 1 };
        return {
          id: entry.id,
          weight: Number(entry.weight || 0)
        };
      })
      .filter((entry) => entry.id && entry.weight > 0);
  }

  function rememberRecentTrack(trackId) {
    if (!trackId) return;
    const existingIndex = recentTrackIds.indexOf(trackId);
    if (existingIndex >= 0) recentTrackIds.splice(existingIndex, 1);
    recentTrackIds.unshift(trackId);
    if (recentTrackIds.length > 4) recentTrackIds.length = 4;
  }

  function recencyPenalty(trackId) {
    const recentIndex = recentTrackIds.indexOf(trackId);
    if (recentIndex === 0) return 0.08;
    if (recentIndex === 1) return 0.26;
    if (recentIndex === 2) return 0.52;
    if (recentIndex === 3) return 0.74;
    return 1;
  }

  function updateBlendState(delta) {
    const safeDelta = Math.max(0, Number(delta) || 0);
    const dimension = context.dimension === 'nether' || context.dimension === 'end'
      ? context.dimension
      : 'overworld';

    for (const dim of DIMENSIONS) {
      const target = dim === dimension ? 1 : 0;
      blendState.dimensions[dim] = damp(blendState.dimensions[dim], target, safeDelta, 1.85);
    }

    const threat = Math.max(0, Number(context.threat || 0));
    const targetTense = THREE.MathUtils.clamp((threat - 0.7) / 1.6, 0, 1);
    const targetForest = context.biome === 'forest'
      ? THREE.MathUtils.clamp(0.38 + ((1 - targetTense) * 0.36), 0.3, 0.75)
      : 0;
    const targetCalm = THREE.MathUtils.clamp(1 - targetTense * 0.92, 0.14, 1);

    blendState.moods.calm = damp(blendState.moods.calm, targetCalm, safeDelta, 1.95);
    blendState.moods.forest = damp(blendState.moods.forest, targetForest, safeDelta, 1.55);
    blendState.moods.tense = damp(blendState.moods.tense, targetTense, safeDelta, 2.05);
  }

  function randomSilenceSeconds(initial = false) {
    if (initial) return 8 + Math.random() * 10;

    const tenseRatio = THREE.MathUtils.clamp(blendState.moods.tense, 0, 1);
    const minSilence = THREE.MathUtils.lerp(32, 14, tenseRatio);
    const maxSilence = THREE.MathUtils.lerp(80, 34, tenseRatio);
    return minSilence + Math.random() * (maxSilence - minSilence);
  }

  function resolveWeightedCandidates() {
    const weightByTrackId = new Map();
    const contributions = {
      overworld: 0,
      overworld_forest: 0,
      overworld_tense: 0,
      nether: 0,
      end: 0
    };

    function addPool(poolId, scalar) {
      const scale = Number(scalar || 0);
      if (!Number.isFinite(scale) || scale <= 0.0001) return;
      const entries = getPoolEntries(poolId);
      if (entries.length <= 0) return;

      contributions[poolId] = (contributions[poolId] || 0) + scale;

      for (const entry of entries) {
        if (!trackStates.has(entry.id)) continue;
        const weight = Math.max(0, Number(entry.weight || 0));
        if (weight <= 0) continue;
        const mixedWeight = weight * scale;
        weightByTrackId.set(entry.id, (weightByTrackId.get(entry.id) || 0) + mixedWeight);
      }
    }

    const overworldWeight = THREE.MathUtils.clamp(blendState.dimensions.overworld, 0, 1);
    const netherWeight = THREE.MathUtils.clamp(blendState.dimensions.nether, 0, 1);
    const endWeight = THREE.MathUtils.clamp(blendState.dimensions.end, 0, 1);

    if (overworldWeight > 0.0001) {
      const calm = Math.max(0, blendState.moods.calm);
      const forest = Math.max(0, blendState.moods.forest);
      const tense = Math.max(0, blendState.moods.tense);
      const total = Math.max(0.0001, calm + forest + tense);

      addPool('overworld', overworldWeight * (calm / total));
      addPool('overworld_forest', overworldWeight * (forest / total));
      addPool('overworld_tense', overworldWeight * (tense / total));
    }
    addPool('nether', netherWeight);
    addPool('end', endWeight);

    const rawCandidates = [];
    for (const [id, weight] of weightByTrackId.entries()) {
      if (weight <= 0.00001) continue;
      rawCandidates.push({ id, weight });
    }

    let dominantPoolId = 'overworld';
    let dominantPoolWeight = -1;
    for (const [poolId, poolWeight] of Object.entries(contributions)) {
      if (poolWeight > dominantPoolWeight) {
        dominantPoolWeight = poolWeight;
        dominantPoolId = poolId;
      }
    }

    return {
      rawCandidates,
      contributions,
      dominantPoolId,
      dominantPoolLabel: getPoolLabel(dominantPoolId)
    };
  }

  const trackStates = new Map();
  for (const [id, cfg] of Object.entries(BGM_TRACKS)) {
    const src = `${MUSIC_ASSET_BASE_PATH}${cfg.file}`;
    const audio = new Audio(src);
    audio.loop = false;
    audio.preload = 'auto';
    audio.volume = 0;
    trackStates.set(id, {
      audio,
      baseVolume: cfg.baseVolume,
      currentVolume: 0
    });
  }

  let unlocked = false;
  let pauseScale = 1;
  let masterVolume = 1;
  let silenceTimer = randomSilenceSeconds(true);
  let playingTrackId = null;
  let lastPlayedTrackId = null;
  let context = {
    dimension: 'overworld',
    biome: null,
    threat: 0
  };

  function pickNextTrackId() {
    if (!lastPlayedTrackId) {
      const openingCandidates = OPENING_TRACK_IDS.filter((id) => trackStates.has(id));
      if (openingCandidates.length > 0) {
        return openingCandidates[Math.floor(Math.random() * openingCandidates.length)];
      }
    }

    const resolved = resolveWeightedCandidates();
    const weightedCandidates = [];
    let totalWeight = 0;

    for (const candidate of resolved.rawCandidates) {
      let weight = Math.max(0, Number(candidate.weight || 0));
      if (weight <= 0.00001) continue;
      weight *= recencyPenalty(candidate.id);
      if (candidate.id === lastPlayedTrackId && resolved.rawCandidates.length > 1) {
        weight *= 0.72;
      }
      if (weight <= 0.00001) continue;
      weightedCandidates.push({
        id: candidate.id,
        weight
      });
      totalWeight += weight;
    }

    weightedCandidates.sort((a, b) => b.weight - a.weight);
    poolTelemetry = {
      dominantPoolId: resolved.dominantPoolId,
      dominantPoolLabel: resolved.dominantPoolLabel,
      contributions: resolved.contributions,
      topCandidates: weightedCandidates.slice(0, 5)
    };

    if (weightedCandidates.length <= 0 || totalWeight <= 0.00001) return null;
    if (weightedCandidates.length === 1) return weightedCandidates[0].id;

    let roll = Math.random() * totalWeight;
    for (const candidate of weightedCandidates) {
      roll -= candidate.weight;
      if (roll <= 0) return candidate.id;
    }
    return weightedCandidates[weightedCandidates.length - 1]?.id || null;
  }

  function scheduleNextTrack(initial = false) {
    silenceTimer = randomSilenceSeconds(initial);
  }

  function tryStartNextTrack() {
    if (!unlocked || playingTrackId) return;
    if (silenceTimer > 0) return;

    const nextTrackId = pickNextTrackId();
    if (!nextTrackId) {
      scheduleNextTrack(false);
      return;
    }

    const state = trackStates.get(nextTrackId);
    if (!state) {
      scheduleNextTrack(false);
      return;
    }

    state.audio.currentTime = 0;
    playingTrackId = nextTrackId;
    lastPlayedTrackId = nextTrackId;
    rememberRecentTrack(nextTrackId);
    state.audio.play().catch(() => {
      if (playingTrackId === nextTrackId) playingTrackId = null;
      scheduleNextTrack(false);
    });
  }

  for (const [trackId, state] of trackStates.entries()) {
    state.audio.addEventListener('ended', () => {
      if (playingTrackId !== trackId) return;
      playingTrackId = null;
      scheduleNextTrack(false);
    });
  }

  function setContext({
    dimension = 'overworld',
    biome = null,
    threat = 0,
    paused = false
  } = {}) {
    const normalizedDimension = dimension === 'nether' || dimension === 'end'
      ? dimension
      : 'overworld';
    context = {
      dimension: normalizedDimension,
      biome,
      threat
    };
    pauseScale = paused ? 0.32 : 1;
  }

  function unlock() {
    if (unlocked) return;
    unlocked = true;
    if (!playingTrackId && silenceTimer <= 0) {
      scheduleNextTrack(true);
    }
  }

  function update(delta) {
    const safeDelta = Math.max(0, Number(delta) || 0);
    updateBlendState(safeDelta);
    if (!playingTrackId) silenceTimer = Math.max(0, silenceTimer - safeDelta);
    tryStartNextTrack();

    const fadeStep = safeDelta * 0.42;
    for (const [id, state] of trackStates.entries()) {
      const active = id === playingTrackId;
      const targetVolume = active ? state.baseVolume * pauseScale * masterVolume : 0;

      if (state.currentVolume < targetVolume) {
        state.currentVolume = Math.min(targetVolume, state.currentVolume + fadeStep);
      } else if (state.currentVolume > targetVolume) {
        state.currentVolume = Math.max(targetVolume, state.currentVolume - fadeStep);
      }

      state.audio.volume = THREE.MathUtils.clamp(state.currentVolume, 0, 1);
      if (!active && state.currentVolume <= 0.001 && !state.audio.paused) {
        state.audio.pause();
      }
    }
  }

  return {
    setContext,
    setMasterVolume(value = 1) {
      masterVolume = THREE.MathUtils.clamp(Number(value) || 0, 0, 1);
    },
    getMasterVolume() {
      return masterVolume;
    },
    unlock,
    update,
    getDebugSnapshot() {
      const activeState = playingTrackId ? trackStates.get(playingTrackId) : null;
      const topCandidates = poolTelemetry.topCandidates.map((entry) => ({
        id: entry.id,
        name: BGM_TRACKS[entry.id]?.title || BGM_TRACKS[entry.id]?.file || entry.id,
        weight: entry.weight
      }));

      return {
        unlocked,
        activeTrackId: playingTrackId,
        activeTrackName: playingTrackId
          ? (BGM_TRACKS[playingTrackId]?.title || BGM_TRACKS[playingTrackId]?.file || playingTrackId)
          : null,
        activeTrackStyle: playingTrackId ? (BGM_TRACKS[playingTrackId]?.style || 'unknown') : null,
        currentVolume: activeState?.currentVolume || 0,
        targetVolume: playingTrackId
          ? (Math.max(0, Number(BGM_TRACKS[playingTrackId]?.baseVolume || 0)) * pauseScale * masterVolume)
          : 0,
        silenceTimer,
        pauseScale,
        masterVolume,
        dominantPoolId: poolTelemetry.dominantPoolId,
        dominantPoolLabel: poolTelemetry.dominantPoolLabel,
        poolContribution: { ...poolTelemetry.contributions },
        dimensionBlend: { ...blendState.dimensions },
        moodBlend: { ...blendState.moods },
        topCandidates
      };
    }
  };
}
